import React, {
  ComponentPropsWithoutRef,
  ElementType,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ACCESSIBLE_ELEMENTS_QUERY } from '../MegaMenu/MegaMenuConstants';
import { JabraFocusItemElement } from './JabraFocus.model';
import { JabraFocusContext, useFocusContext } from './JabraFocusContext';
import { getMoveDelta, useArrowKeyHandler } from './JabraFocusUtils';

export interface JabraFocusGroupProps {
  orientation?: 'vertical' | 'horizontal' | 'both';
  children: ReactNode;
  loop?: boolean;
  id?: string;
  className?: string;
}

export const JabraFocusGroup = ({
  children,
  orientation = 'both',
  loop,
  className,
  id: initialID,
  onKeyDown,
  ...rest
}: JabraFocusGroupProps & ComponentPropsWithoutRef<'div'>) => {
  const [items, setItems] = useState<JabraFocusItemElement[]>([]);
  const [focusedItemId, setFocusedItemId] = useState<string | undefined>(undefined);
  const parentContext = useFocusContext();
  const container = useRef<HTMLDivElement>(null);

  const {
    registerItem: parentRegister,
    unregisterItem: parentUnregister,
    setFocused: parentSetFocused,
  } = parentContext ?? {};

  const genericID = useId();
  const id = initialID ?? genericID;

  const moveFocus = useCallback(
    (delta: number): boolean => {
      if (!container.current) return false;

      const elements = container.current.querySelectorAll('[data-focus-item]');

      const orderedItems: JabraFocusItemElement[] = [];
      elements.forEach((el) => {
        const foundItem = items.find((itm) => itm.id === el.id);
        if (!foundItem) {
          return;
        }
        orderedItems.push(foundItem);
      });

      if (orderedItems.length === 0) return false;

      const focusedIndex = orderedItems.findIndex((itm) => itm.id === focusedItemId);

      if (focusedIndex === -1) {
        const [first] = orderedItems;
        first.focus();
        return true;
      }

      const nextIndex = focusedIndex + delta;

      if (!loop && (nextIndex < 0 || nextIndex + 1 > orderedItems.length)) {
        return false;
      }

      const safeIndex = (orderedItems.length + focusedIndex + delta) % orderedItems.length;

      orderedItems[safeIndex].focus();
      return true;
    },
    [items, focusedItemId, loop]
  );
  const api = useMemo(() => {
    return {
      focus: () => {
        moveFocus(0);
      },
      id,
    };
  }, [moveFocus, id]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const delta = getMoveDelta(event.key, orientation);
    if (delta === undefined) {
      return;
    }

    if (delta !== 0 && moveFocus(delta)) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!parentContext.parentId) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  useEffect(() => {
    if (!parentRegister) return;
    parentRegister(api);
  }, [api, parentRegister, id]);

  useEffect(() => {
    return () => {
      parentUnregister(id);
    };
  }, [parentUnregister, id]);

  const registerItem = useCallback((newItem: JabraFocusItemElement) => {
    setItems((currentItems) => {
      if (currentItems.some((itm) => itm.id === newItem.id)) {
        return currentItems.map((itm) => {
          if (itm.id === newItem.id) {
            return newItem;
          }
          return itm;
        });
      }
      return [...currentItems, newItem];
    });
  }, []);

  const unregisterItem = useCallback((id: string) => {
    setItems((currentItems) => currentItems.filter((itm) => itm.id !== id));
  }, []);

  const setFocused = useCallback(
    (itemId: string) => {
      setFocusedItemId(itemId);
      if (parentSetFocused) {
        parentSetFocused(id);
      }
    },
    [id, parentSetFocused]
  );

  const contextValue = useMemo(() => {
    return {
      moveFocus,
      registerItem,
      unregisterItem,
      setFocused,
      focused: focusedItemId,
      items,
      parentId: id,
      orientation: orientation ?? 'both',
    };
  }, [moveFocus, orientation, unregisterItem, registerItem, setFocused, focusedItemId, items, id]);

  return (
    <div
      onKeyDown={handleKeyDown}
      ref={container}
      id={id}
      data-focus-item={true}
      className={className}
      {...rest}
    >
      <JabraFocusContext.Provider value={contextValue}>{children} </JabraFocusContext.Provider>
    </div>
  );
};

export interface JabraFocusItemProps {
  children: ReactElement;
  onFocus?: () => void;
  asChild?: boolean;
  className?: string;
  as?: ElementType;
}

export const JabraFocusItem = ({
  children,
  onFocus,
  asChild,
  className,
  as: Tag = 'div',
}: JabraFocusItemProps) => {
  const id = useId();
  const { registerItem, unregisterItem, setFocused } = useFocusContext();

  const element = useRef<HTMLDivElement>(null);

  const api = useMemo(() => {
    return {
      focus: () => {
        if (!element.current) return;

        const isFocusable = ['A', 'BUTTON', 'INPUT'].includes(element.current.tagName);
        if (isFocusable) {
          element.current.focus();
        } else {
          (element.current.querySelector(ACCESSIBLE_ELEMENTS_QUERY) as HTMLElement)?.focus();
        }

        if (onFocus) {
          onFocus();
        }
      },
      id,
    };
  }, [id, onFocus]);

  useEffect(() => {
    registerItem(api);
  }, [api, registerItem, id]);

  useEffect(() => {
    return () => {
      unregisterItem(id);
    };
  }, [unregisterItem, id]);

  const handleOnFocus = () => {
    setFocused(id);
  };

  const handleKeyDown = useArrowKeyHandler();

  if (asChild) {
    const clonedProps = {
      ...(children.props || {}),
      id,
      onKeyDown: handleKeyDown,
      ref: element,
      onFocus: handleOnFocus,
      'data-focus-item': true,
    };

    return React.cloneElement(children, clonedProps);
  }
  return (
    <Tag
      className={className}
      data-focus-item={true}
      id={id}
      ref={element}
      onKeyDown={handleKeyDown}
      onFocus={handleOnFocus}
    >
      {children}
    </Tag>
  );
};
